import { Box, Flex, Image, useDisclosure } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import FilterIcon from '../assets/icons/FilterIcon.svg';
import FilterIconWhite from '../assets/icons/FilterIconWhite.svg';
import { AuthenticatedComponent } from '../auth/keycloak';
import {
    DashboardSessionsFilterDrawer,
    FILTER_1000_PLAYER,
    FILTER_100_1000_PLAYER,
    FILTER_10_100_PLAYER,
    FILTER_10_PLAYER,
    FILTER_ALL,
    FILTER_BINGO,
    FILTER_LEVIATHAN,
    FILTER_MARBLES,
    FILTER_MOST_RECENT,
    FILTER_OLDEST,
    FILTER_RDR,
} from '../component/gameComponents/DashboardSessionsFilterDrawer';
import { PastSixListItem } from '../component/gameComponents/PastSixListItem';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { CommonScrollList } from '../component/ScrollList';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { getSessionsWithPlayerCount, SessionDataWithPlayerCount } from '../service/analytics';
import {
    BINGO_LICENSE,
    LEVIATHAN_LICENSE,
    MARBLES_LICENSE,
    RACE_DAY_LICENSE,
} from '../service/games';
import { PaginatedData } from '../service/util';

const LogPrefix = 'DashboardSessions';

export const DashboardSessionsPage: AuthenticatedComponent = ({ keycloak }) => {
    const FILTER_LICENSE_MAP: { [filter: string]: string } = {
        [FILTER_ALL]: '',
        [FILTER_MARBLES]: MARBLES_LICENSE,
        [FILTER_BINGO]: BINGO_LICENSE,
        [FILTER_RDR]: RACE_DAY_LICENSE,
        [FILTER_LEVIATHAN]: LEVIATHAN_LICENSE,
    };

    const { isLoadFromCache } = useContext(PageDataContext);
    const { isOpen, onOpen, onClose } = useDisclosure();

    type ItemType = SessionDataWithPlayerCount;
    const [allItems, setItems] = usePageDataCache<{ [date: string]: ItemType[] }>('allItems', {});
    const [sortedDates, setSortedDates] = usePageDataCache<string[]>('sortedDates', []);
    const [pagination, setPagination] = usePageDataCache<PaginatedData | null>('pagination', null);
    const [hasMore, setHasMore] = usePageDataCache<boolean>('hasMore', false);
    const [hasError, setHasError] = usePageDataCache<boolean>('hasError', false);
    const [gameTitleFilter, setGameTitleFilter] = usePageDataCache<string>(
        'gameTitleFilter',
        FILTER_ALL,
    );
    const [gameDurationFilter, setGameDurationFilter] = usePageDataCache<string>(
        'gameDurationFilter',
        FILTER_MOST_RECENT,
    );
    const [gameAudienceSizeFilter, setGameAudienceSizeFilter] = usePageDataCache<string>(
        'gameAudienceSizeFilter',
        FILTER_ALL,
    );
    const [isFetchingData, setIsFetchingData] = useState<Array<boolean>>([]);
    const [abortController, setAbortController] = useState<AbortController>();

    const updateGameDurationFilter = (duration: string) => {
        const logPrefix = `[${LogPrefix}.updateGameDurationFilter]`;
        setGameDurationFilter(duration);
        console.log(`${logPrefix} set game duration filter to: ${duration}`);
    };

    const updateGameTitleFilter = (gameTitle: string) => {
        const logPrefix = `[${LogPrefix}.updateGameTitleFilter]`;
        setGameTitleFilter(gameTitle);
        console.log(`${logPrefix} set game title filter to: ${gameTitle}`);
    };

    const updateGameAudienceSizeFilter = (size: string) => {
        const logPrefix = `[${LogPrefix}.updateGameAudienceSizeFilter]`;
        setGameAudienceSizeFilter(size);
        console.log(`${logPrefix} set game audience size filter to: ${size}`);
    };

    const applyFilters = () => {
        onClose();

        const license = FILTER_LICENSE_MAP[gameTitleFilter];
        const duration = gameDurationFilter;
        const size = gameAudienceSizeFilter;

        fetchFreshSessionData(license, duration, size);
    };

    const resetFilters = () => {
        setGameTitleFilter(FILTER_ALL);
        setGameDurationFilter(FILTER_ALL);
        setGameAudienceSizeFilter(FILTER_ALL);
        onClose();

        const license = '';
        const duration = FILTER_ALL;
        const size = FILTER_ALL;
        fetchFreshSessionData(license, duration, size);
    };

    const getFilterIcon = () => {
        if (isOpen) {
            return FilterIcon;
        } else {
            return FilterIconWhite;
        }
    };

    const fetchFreshSessionData = async (license: string, duration: string, size: string) => {
        abortController?.abort();
        setItems({});
        setPagination(null);
        setHasMore(false);

        fetchSessionData(true, '', license, duration, size);
    };

    const fetchMoreSessionData = async () => {
        const nextPage = pagination?.next || '';
        const license = FILTER_LICENSE_MAP[gameTitleFilter];
        const duration = gameDurationFilter;
        const size = gameAudienceSizeFilter;
        fetchSessionData(false, nextPage, license, duration, size);
    };

    const fetchSessionData = async (
        reset: boolean,
        nextPage: string,
        license: string,
        duration: string,
        size: string,
    ) => {
        const logPrefix = `[${LogPrefix}._fetchSessionData]`;

        const userID = keycloak.subject;

        if (isFetchingData.length > 0 && !reset) {
            console.log(`${logPrefix} already fetching, skip request`);
            return;
        }
        isFetchingData.push(true);
        setIsFetchingData([...isFetchingData]);

        const newController = new AbortController();
        setAbortController(newController);

        console.log(`${logPrefix} starting with reset: ${reset}`);

        try {
            const session_id = '';
            let sort_order = '';
            let player_size_min = 0;
            let player_size_max = 0;
            switch (duration) {
                case FILTER_ALL:
                    // do nothing
                    break;

                case FILTER_MOST_RECENT:
                    sort_order = `desc`;
                    break;
                case FILTER_OLDEST:
                    sort_order = `asc`;
                    break;
            }

            switch (size) {
                case FILTER_ALL:
                    // do nothing
                    break;
                case FILTER_10_PLAYER:
                    player_size_max = 10;
                    break;
                case FILTER_10_100_PLAYER:
                    player_size_min = 10;
                    player_size_max = 100;
                    break;
                case FILTER_100_1000_PLAYER:
                    player_size_min = 100;
                    player_size_max = 1000;
                    break;
                case FILTER_1000_PLAYER:
                    player_size_min = 1000;
                    break;
            }

            // query sessions from lobby
            const pastSessions = await getSessionsWithPlayerCount(
                session_id,
                license,
                userID,
                player_size_min,
                player_size_max,
                sort_order,
                nextPage,
                keycloak.token,
            );

            console.log(`${logPrefix} receive: ${JSON.stringify(pastSessions)}`);

            setHasMore(pastSessions?.page.next !== undefined && pastSessions?.page.next !== '');
            setPagination(pastSessions?.page || null);

            let items = allItems;
            if (reset) {
                items = {};
            }

            const toAdd = pastSessions?.data || [];
            toAdd.forEach((x) => {
                const thisDate = new Date(x.room_created_ms);
                const dateString = thisDate.toLocaleDateString();
                let list = items[dateString];
                if (!list) {
                    list = [];
                    items[dateString] = list;
                }

                // scrub duplicate sessions
                if (!list.find((y) => y.session_id === x.session_id)) {
                    list.push(x);
                }
            });
            setItems({ ...items });

            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(false);
            console.log(`${logPrefix} done with reset: ${reset}`);
        } catch (ex) {
            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(true);
            console.error(`${logPrefix} ${ex}`);
        }
    };

    const hasMoreData = (hasMore: boolean): boolean => {
        const logPrefix = `[${LogPrefix}.hasMoreData]`;
        console.log(`${logPrefix} hasMore: ${hasMore} isFetchingData: ${isFetchingData.length}`);
        return hasMore || isFetchingData.length > 0;
    };

    const renderContent = () => (
        <Flex id={'session-scroll'} className={'dashboard-container'}>
            <CommonScrollList
                loading={isFetchingData.length > 0}
                onLoadMore={fetchMoreSessionData}
                hasMore={hasMoreData(hasMore)}
                hasError={hasError}
                onRefresh={async () => {
                    const license = FILTER_LICENSE_MAP[gameTitleFilter];
                    const duration = gameDurationFilter;
                    const size = gameAudienceSizeFilter;
                    await fetchFreshSessionData(license, duration, size);
                }}
                children={sortedDates.map((date, idx) => {
                    const items = allItems[date];
                    return (
                        <PastSixListItem
                            sessionDatas={items && [...items]}
                            index={idx}
                            date={date}
                            key={idx}
                        />
                    );
                })}
            />
            <DashboardSessionsFilterDrawer
                onClose={onClose}
                isOpen={isOpen}
                resetFilters={resetFilters}
                applyFilters={applyFilters}
                updateGameAudienceSizeFilter={updateGameAudienceSizeFilter}
                gameAudienceSizeFilter={gameAudienceSizeFilter}
                updateGameDurationFilter={updateGameDurationFilter}
                gameDurationFilter={gameDurationFilter}
                updateGameTitleFilter={updateGameTitleFilter}
                gameTitleFilter={gameTitleFilter}
            />
        </Flex>
    );

    useEffect(() => {
        // order by date descending
        setSortedDates(
            Array.from(Object.keys(allItems)).sort((n1, n2) => {
                if (gameDurationFilter === FILTER_MOST_RECENT) {
                    return n1 > n2 ? -1 : 1;
                } else {
                    return n1 > n2 ? 1 : -1;
                }
            }),
        );
    }, [allItems]);

    useEffect(() => {
        if (!isLoadFromCache) {
            const license = FILTER_LICENSE_MAP[gameTitleFilter];
            const duration = gameDurationFilter;
            const size = gameAudienceSizeFilter;
            fetchFreshSessionData(license, duration, size);
        }
    }, []);

    return (
        <>
            <BrowserView>
                <Box>{renderContent()}</Box>
            </BrowserView>
            <MobileViewPage
                title="SESSIONS"
                titleColor="#FFF"
                titleBackgroundColor="#6867dd"
                isLoading={isFetchingData.length > 0}
            >
                <Flex className="tool-bar">
                    <Flex className={'search-bar'}>
                        <Flex className="filter-circle">
                            <Image
                                className={'filter-icon'}
                                src={getFilterIcon()}
                                onClick={onOpen}
                            />
                        </Flex>
                    </Flex>
                </Flex>
                {renderContent()}
            </MobileViewPage>
        </>
    );
};
